"use client";

import { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';
import Image from "next/image";
import { useBranding } from '@/contexts/branding-context';
import { useAuth } from '@/contexts/auth-context';

/**
 * The app menu button's face — used by the sidebar trigger and both sidebar
 * headers.
 *
 * A plain menu icon for everyone (Gordon 2026-11-03: "stop the app menu
 * button slideshow, only icon"). The content-thumbnail slideshow is gone.
 * A signed-in person can still put a STATIC picture there in Profile → App
 * menu button: their own logo ('own') or the BB logo ('appLogo'); the old
 * 'app' (slideshow) mode now shows the icon.
 */
const AnimatedLogo = () => {
  const { logoUrl, appName } = useBranding();
  const { user } = useAuth();

  const [personalLogo, setPersonalLogo] = useState('');
  const [logoMode, setLogoMode] = useState('');
  // Re-read when Profile → App menu button saves a new choice.
  const [rev, setRev] = useState(0);
  useEffect(() => {
    const bump = () => setRev(r => r + 1);
    window.addEventListener('bb-logo-button', bump);
    return () => window.removeEventListener('bb-logo-button', bump);
  }, []);
  useEffect(() => {
    if (!user) { setPersonalLogo(''); setLogoMode(''); return; }
    let cancelled = false;
    import('@/services/app-logo')
      .then(m => m.getUserLogoSettings(user.uid))
      .then(s => {
        if (cancelled) return;
        setPersonalLogo(s.logoUrl || '');
        setLogoMode(s.logoMode || '');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, rev]);

  const picture = !user ? ''
    : logoMode === 'own' ? personalLogo
    : logoMode === 'appLogo' ? logoUrl
    : '';

  if (!picture) {
    return (
      <div className="relative flex h-full w-full items-center justify-center bg-transparent text-foreground" aria-hidden="true">
        <Menu className="!size-6" />
      </div>
    );
  }

  return (
    <div className="relative h-full w-full bg-transparent overflow-hidden">
      <Image
        src={picture}
        alt={`${appName} Logo`}
        fill
        unoptimized={picture.startsWith('http')}
        className="object-contain"
        priority
      />
    </div>
  );
};

export default AnimatedLogo;
